import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Nav, Tab, Table, Badge, Spinner, Alert, Button, Row, Col, Form } from 'react-bootstrap';
import { rmApi } from '../../services/api';
import { 
  FaArrowLeft, 
  FaSpinner, 
  FaExclamationTriangle, 
  FaFileInvoice, 
  FaListAlt, 
  FaEye, 
  FaSearch, 
  FaBriefcase, 
  FaCheck,
  FaTimes, 
  FaCheckCircle
} from 'react-icons/fa';

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString; // Fallback
  }
};

const formatDateExtractMonth = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedMonth = date.toLocaleDateString('en-GB', {
      month: 'long'
    });
    return `${formattedMonth}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

function PayoutDetails() {
  const { payoutId } = useParams();
  const [payoutDetails, setPayoutDetails] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [dsoCases, setDsoCases] = useState([]);
  const [loading, setLoading] = useState({
    details: true,
    invoices: true,
    dsoCases: false,
    action: false
  });
  const [error, setError] = useState({
    details: '',
    invoices: '',
    dsoCases: '',
    action: ''
  });
  const [activeTab, setActiveTab] = useState('dso-cases');
  const [caseSearchTerm, setCaseSearchTerm] = useState('');
  // Set default status filter to show only Approved (2) and Disbursed (3) cases
  const [caseStatusFilter, setCaseStatusFilter] = useState('approved_disbursed');
  const [casePage, setCasePage] = useState(0);
  const caseLimit = 10;
  
  // State for selected cases
  const [selectedCases, setSelectedCases] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');

  // Fetch payout details
  useEffect(() => {
    const fetchPayoutDetails = async () => {
      setLoading(prev => ({ ...prev, details: true }));
      setError(prev => ({ ...prev, details: '' }));
      
      try {
        const response = await rmApi.getPayoutDetails(payoutId);
        setPayoutDetails(response.data);
      } catch (err) {
        console.error("Error fetching payout details:", err);
        setError(prev => ({ ...prev, details: 'Failed to load payout details.' }));
      } finally {
        setLoading(prev => ({ ...prev, details: false }));
      }
    };

    fetchPayoutDetails();
  }, [payoutId]);


  // Fetch invoices when tab is active
  useEffect(() => {
    const fetchInvoices = async () => {
      if (activeTab !== 'invoices') return;
      
      setLoading(prev => ({ ...prev, invoices: true }));
      setError(prev => ({ ...prev, invoices: '' }));
      
      try {
        const response = await rmApi.getPayoutInvoices(payoutId);
        setInvoices(response.data.items || []);
      } catch (err) {
        console.error("Error fetching payout invoices:", err);
        setError(prev => ({ ...prev, invoices: 'Failed to load invoices.' }));
      } finally {
        setLoading(prev => ({ ...prev, invoices: false }));
      }
    };

    fetchInvoices();
  }, [payoutId, activeTab]);

  // Fetch DSO cases when tab is active
  useEffect(() => {
    const fetchDsoPayoutCases = async () => {
      if (activeTab !== 'dso-cases' || !payoutDetails?.payment_requester_id) return;
      
      setLoading(prev => ({ ...prev, dsoCases: true }));
      setError(prev => ({ ...prev, dsoCases: '' }));
      
      try {
        // Handle the special approved_disbursed filter
        let statusFilterParam = caseStatusFilter;
        
        if (caseStatusFilter === 'approved_disbursed') {
          statusFilterParam = '';
        }
        
        const response = await rmApi.getPayoutCases(
          payoutId, 
          {
            skip: casePage * caseLimit,
            limit: caseLimit,
            search: caseSearchTerm,
            status_filter: statusFilterParam
          }
        );
        
        // If we're using the special filter, filter the results client-side
        if (caseStatusFilter === 'approved_disbursed') {
          // Filter to only keep status 2 (Approved) and 3 (Disbursed) cases
          const filteredCases = (response.data.items || []).filter(caseItem => 
            caseItem.case_status === 12 || caseItem.case_status === 13
          );
          setDsoCases(filteredCases);
        } else {
          setDsoCases(response.data.items || []);
        }
      } catch (err) {
        console.error("Error fetching DSO cases:", err);
        setError(prev => ({ ...prev, dsoCases: 'Failed to load DSO cases.' }));
      } finally {
        setLoading(prev => ({ ...prev, dsoCases: false }));
      }
    };

    fetchDsoPayoutCases();
  }, [payoutDetails?.payment_requester_id, activeTab, casePage, caseSearchTerm, caseStatusFilter]);

  // Function to handle case search
  const handleCaseSearch = (e) => {
    e.preventDefault();
    setCasePage(0); // Reset to first page when searching
    // The search term is already updated via the input onChange
  };

  // Handle checkbox selection
  const handleSelectCase = (caseId) => {
    if (selectedCases.includes(caseId)) {
      setSelectedCases(selectedCases.filter(id => id !== caseId));
    } else {
      setSelectedCases([...selectedCases, caseId]);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCases([]); // Deselect all
    } else {
      setSelectedCases(dsoCases.map(caseItem => caseItem.id)); // Select all
    }
    setSelectAll(!selectAll);
  };

  // Reset selections when cases change
  useEffect(() => {
    setSelectedCases([]);
    setSelectAll(false);
  }, [dsoCases]);

  // Handle approving selected cases
  const handleApproveCases = async () => {
    if (selectedCases.length === 0) {
      setError(prev => ({ ...prev, action: 'Please select at least one lead to approve.' }));
      return;
    }

    setLoading(prev => ({ ...prev, action: true }));
    setError(prev => ({ ...prev, action: '' }));
    setActionSuccess('');

    try {
      // In a real implementation, this would call an API endpoint
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Success message
      setActionSuccess(`Successfully approved ${selectedCases.length} lead(s).`);
      
      // Refresh the list
      const response = await rmApi.getPayoutCases(
        payoutId, 
        {
          skip: casePage * caseLimit,
          limit: caseLimit,
          search: caseSearchTerm,
          status_filter: caseStatusFilter
        }
      );
      setDsoCases(response.data.items || []);
      
      // Clear selections
      setSelectedCases([]);
      setSelectAll(false);
    } catch (err) {
      console.error("Error approving cases:", err);
      setError(prev => ({ ...prev, action: 'Failed to approve leads. Please try again.' }));
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  // Handle disapproving selected cases
  const handleDisapproveCases = async () => {
    if (selectedCases.length === 0) {
      setError(prev => ({ ...prev, action: 'Please select at least one lead to disapprove.' }));
      return;
    }

    setLoading(prev => ({ ...prev, action: true }));
    setError(prev => ({ ...prev, action: '' }));
    setActionSuccess('');

    try {
      // In a real implementation, this would call an API endpoint
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Success message
      setActionSuccess(`Successfully disapproved ${selectedCases.length} lead(s).`);
      
      // Refresh the list
      const response = await rmApi.getPayoutCases(
        payoutId, 
        {
          skip: casePage * caseLimit,
          limit: caseLimit,
          search: caseSearchTerm,
          status_filter: caseStatusFilter
        }
      );
      setDsoCases(response.data.items || []);
      
      // Clear selections
      setSelectedCases([]);
      setSelectAll(false);
    } catch (err) {
      console.error("Error disapproving cases:", err);
      setError(prev => ({ ...prev, action: 'Failed to disapprove leads. Please try again.' }));
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  // Function to render status badge
  const renderStatusBadge = (status) => {
    let badgeClass = '';
    switch (status?.toLowerCase()) {
      case 'disbursed':
        badgeClass = 'success';
        break;
      case 'approved':
        badgeClass = 'primary';
        break;
      case 'created':
        badgeClass = 'warning';
        break;
      default:
        badgeClass = 'secondary';
    }
    return <Badge bg={badgeClass}>{status || 'Unknown'}</Badge>;
  };

  return (
    <div className="rm-payout-details-page">
      <div className="page-header">
        <Link to="/rm/payouts" className="back-link">
          <FaArrowLeft /> Back to Payouts
        </Link>
        <h2>Payout Details</h2>
      </div>

      {loading.details ? (
        <div className="loading-state">
          <Spinner animation="border" role="status" variant="primary" />
          <span>Loading payout details...</span>
        </div>
      ) : error.details ? (
        <Alert variant="danger">
          <FaExclamationTriangle className="me-2" />
          {error.details}
        </Alert>
      ) : payoutDetails ? (
        <>
          {/* Payout Summary Card */}
          <Card className="mb-4 detail-card">
            <Card.Header className="detail-card-header">
              <h4 className="mb-0">Payout Summary</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3} lg={3} className="detail-item">
                  <div className="detail-label">DSO Name</div>
                  <div className="detail-value">{payoutDetails.dso_name}</div>
                </Col>
                <Col md={3} lg={3} className="detail-item">
                  <div className="detail-label">Total Amount</div>
                  <div className="detail-value">{formatCurrency(payoutDetails.total_amount)}</div>
                </Col>
                <Col md={3} lg={3} className="detail-item">
                  <div className="detail-label">Status</div>
                  <div className="detail-value">{renderStatusBadge(payoutDetails.status_str)}</div>
                </Col>
                <Col md={3} lg={2} className="detail-item">
                  <div className="detail-label">Created On</div>
                  <div className="detail-value">{formatDate(payoutDetails.requested_at)}</div>
                </Col>
                <Col md={3} lg={2} className="detail-item">
                  <div className="detail-label">Disbursed On</div>
                  <div className="detail-value">{formatDate(payoutDetails.disbursed_at)}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tabs for Cases, Invoices, and DSO Cases */}
          <Card className="detail-card">
            <Card.Header className="tab-header">
              <Nav variant="tabs">
                <Nav.Item>
                  <Nav.Link 
                    eventKey="dso-cases" 
                    active={activeTab === 'dso-cases'} 
                    onClick={() => setActiveTab('dso-cases')}
                    className="tab-link"
                  >
                    <FaListAlt className="me-2" /> Leads
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    eventKey="invoices" 
                    active={activeTab === 'invoices'} 
                    onClick={() => setActiveTab('invoices')}
                    className="tab-link"
                  >
                    <FaFileInvoice className="me-2" /> Invoices
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body className="tab-content-wrapper">
              {/* Invoices Tab */}
              {activeTab === 'invoices' && (
                <>
                  {loading.invoices ? (
                    <div className="tab-loading">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span>Loading invoices...</span>
                    </div>
                  ) : error.invoices ? (
                    <Alert variant="danger" className="mb-0">
                      <FaExclamationTriangle className="me-2" />
                      {error.invoices}
                    </Alert>
                  ) : invoices.length > 0 ? (
                    <div className="table-responsive">
                      <Table striped hover className="detail-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Invoice No.</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Generated By</th>
                            <th>Generated On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice, index) => (
                            <tr key={invoice.id}>
                              <td>{index + 1}</td>
                              <td>
                                {invoice.invoice_no}
                              </td>
                              <td>{formatCurrency(invoice.amount)}</td>
                              <td>{renderStatusBadge(invoice.payment_status_str)}</td>
                              <td>{invoice.generated_by_name}</td>
                              <td>{formatDate(invoice.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="tab-empty">No invoices found for this payout.</div>
                  )}
                </>
              )}

              {/* Leads Tab */}
              {activeTab === 'dso-cases' && (
                <>
                  <div className="filters-section mb-3">
                    <Form onSubmit={handleCaseSearch}>
                      <Row className="mb-3">
                        <Col md={5}>
                          <Form.Group className="mb-0">
                            <Form.Control
                              type="text"
                              placeholder="Search by lead name or application number"
                              value={caseSearchTerm}
                              onChange={(e) => setCaseSearchTerm(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Button type="submit" variant="primary" className="w-100">
                            <FaSearch className="me-1" /> Search
                          </Button>
                        </Col>
                        <Col md={2}>
                          <Button 
                            type="button" 
                            variant="outline-secondary" 
                            className="w-100"
                            onClick={() => {
                              setCaseSearchTerm('');
                              setCasePage(0);
                            }}
                          >
                            Reset
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  </div>

                  {console.log("dsoCases",dsoCases)}
                  {loading.dsoCases ? (
                    <div className="tab-loading">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span>Loading leads...</span>
                    </div>
                  ) : error.dsoCases ? (
                    <Alert variant="danger" className="mb-0">
                      <FaExclamationTriangle className="me-2" />
                      {error.dsoCases}
                    </Alert>
                  ) : dsoCases.length > 0 ? (
                    <div className="table-responsive">
                      <Table striped hover className="detail-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Lead Name</th>
                            <th>Application No.</th>
                            <th>Product</th>
                            <th>Product Category</th>
                            <th>Lead Status</th>
                            <th>RM Status</th>
                            <th>Month</th>
                            <th>Created On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dsoCases.map((caseItem, index) => (
                            <tr key={caseItem.id}>
                              <td>{casePage * caseLimit + index + 1}</td>
                              <td>{caseItem.lead_name}</td>
                              <td>#{caseItem.id}</td>
                              <td>{caseItem.product_name}</td>
                              <td>{caseItem.case_type}</td>
                              <td>{renderStatusBadge(caseItem.case_status_str)}</td>
                              <td>Approved for Payout <FaCheckCircle/></td>
                              <td>{formatDateExtractMonth(caseItem.created_at)}</td>
                              <td>{formatDate(caseItem.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>

                      <div className="pagination-controls d-flex justify-content-between align-items-center mt-3">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          disabled={casePage === 0}
                          onClick={() => setCasePage(prev => Math.max(0, prev - 1))}
                        >
                          Previous Page
                        </Button>
                        <span className="page-info">Page {casePage + 1}</span>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          disabled={dsoCases.length < caseLimit}
                          onClick={() => setCasePage(prev => prev + 1)}
                        >
                          Next Page
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="tab-empty">No leads found for this DSO.</div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </>
      ) : (
        <Alert variant="warning">Payout not found or you don't have permission to view it.</Alert>
      )}

      <style jsx>{`
        .rm-payout-details-page {
          padding: 20px;
          background-color: #f8fafc;
          max-width: 100%;
          overflow-x: hidden;
        }
        
        .page-header {
          display: flex;
          flex-direction: column;
          margin-bottom: 20px;
        }
        
        .back-link {
          color: #4b5563;
          text-decoration: none;
          margin-bottom: 8px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.9rem;
        }
        
        .back-link:hover {
          color: #1f2937;
        }
        
        .page-header h2 {
          margin: 0;
          font-size: 1.4rem;
          font-weight: 600;
          color: #111827;
        }

        .detail-card {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          border: none;
          border-radius: 8px;
        }
        
        .detail-card-header {
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 16px;
        }
        
        .detail-card-header h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e293b;
        }
        
        .detail-item {
          margin-bottom: 16px;
        }
        
        .detail-label {
          font-size: 0.8rem;
          color: #6b7280;
          margin-bottom: 4px;
        }
        
        .detail-value {
          font-size: 0.95rem;
          font-weight: 500;
          color: #111827;
        }

        .tab-header {
          background-color: #fff;
          padding: 0;
          border-bottom: none;
        }
        
        .tab-link {
          color: #4b5563;
          border: none;
          border-bottom: 2px solid transparent;
          border-radius: 0;
          padding: 12px 16px;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }
        
        .tab-link:hover {
          color: #1e40af;
          background-color: rgba(59, 130, 246, 0.05);
        }
        
        .tab-link.active {
          color: #1e40af;
          border-bottom-color: #1e40af;
          background-color: transparent;
        }
        
        .tab-content-wrapper {
          padding: 16px;
        }
        
        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          gap: 16px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          color: #6b7280;
        }
        
        .tab-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          gap: 10px;
          color: #6b7280;
        }
        
        .tab-empty {
          padding: 24px;
          text-align: center;
          color: #6b7280;
          font-size: 0.9rem;
          background-color: #f9fafb;
          border-radius: 6px;
        }
        
        .table-responsive {
          overflow-x: auto;
          margin: 0 -16px;
          padding: 0 16px;
        }
        
        .detail-table {
          font-size: 0.85rem;
        }
        
        .detail-table th {
          font-weight: 600;
          color: #4b5563;
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
          padding: 10px 16px;
        }
        
        .detail-table td {
          padding: 12px 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        
        /* Style for selected rows */
        .selected-row {
          background-color: rgba(59, 130, 246, 0.05) !important;
        }
        
        .selected-row:hover {
          background-color: rgba(59, 130, 246, 0.1) !important;
        }
        
        .link-primary {
          color: #2563eb;
          text-decoration: none;
          font-weight: 500;
        }
        
        .link-primary:hover {
          text-decoration: underline;
          color: #1d4ed8;
        }
        
        .filters-section {
          padding: 16px;
          background-color: #f9fafb;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .pagination-controls {
          margin-top: 16px;
        }

        .page-info {
          font-size: 0.9rem;
          color: #6b7280;
        }
        
        /* Responsive improvements */
        @media (min-width: 768px) {
          .page-header {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }
          
          .back-link {
            margin-bottom: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default PayoutDetails;